import type { NormalizedChart, PalaceId } from './domain.js';
import { buildPalaceEvidenceViewModel } from './precisionPresentation.js';
import type { TopicId } from './rules.js';

export interface TopicStructuralContextViewModel {
  model: 'TOPIC-STRUCTURAL-CONTEXT-0.1.0';
  topic: TopicId;
  palaceId: PalaceId;
  palaceLabel: string;
  headline: string;
  focalSummary: string;
  relationSummary: string;
  transformationSummary: string;
  readingGuide: string;
  starChips: readonly string[];
  relationChips: readonly string[];
  transformationChips: readonly string[];
  disclosure: string;
}

const TOPIC_PALACE: Partial<Record<TopicId, PalaceId>> = Object.freeze({
  personality: 'life', career: 'career', wealth: 'wealth', relationship: 'spouse',
  social: 'friends', family: 'siblings', home: 'property', mobility: 'travel', wellbeing: 'wellbeing',
});

const TOPIC_HEADLINE: Partial<Record<TopicId,string>> = Object.freeze({
  personality:'命宮與三方四正結構', career:'官祿宮與工作結構', wealth:'財帛宮與資源結構',
  relationship:'夫妻宮與互動結構', social:'僕役宮與合作結構', family:'兄弟宮與家庭協調結構',
  home:'田宅宮與居住結構', mobility:'遷移宮與外部環境結構', wellbeing:'福德宮與內在節奏結構',
});const BRIGHTNESS_LABEL: Readonly<Record<string,string>> = Object.freeze({
  temple:'廟', prosperous:'旺', gain:'得', favorable:'利', neutral:'平', weak:'弱', fallen:'陷', none:'—',
});
const MUTAGEN_LABEL: Readonly<Record<string,string>> = Object.freeze({ lu:'化祿', quan:'化權', ke:'化科', ji:'化忌' });
const RELATION_LABEL: Readonly<Record<string,string>> = Object.freeze({ trine:'三方', opposite:'對宮', adjacent:'鄰宮', derived:'延伸', same:'同宮' });

function starText(star:{label:string;brightness:string;birthYearMutagens:readonly {id:string;label:string}[]}):string {
  const brightness=BRIGHTNESS_LABEL[star.brightness] ?? star.brightness;
  const mutagens=star.birthYearMutagens.map((item)=>item.label).join('、');
  return `${star.label}${brightness && brightness!=='—'?`（${brightness}）`:''}${mutagens?`・${mutagens}`:''}`;
}
function listText(items:readonly string[],empty='無'):string { return items.length?items.join('、'):empty; }
function relationText(item:{palaceLabel:string;relation:string;starLabels:readonly string[]}):string {
  return `${RELATION_LABEL[item.relation]??item.relation}「${item.palaceLabel}」：${listText(item.starLabels,'無主要可列星曜')}`;
}
function transformationText(items:readonly any[]): readonly string[] {
  return Object.freeze(items.map((item:any)=>{
    const mutagen=item.mutagenLabel ?? MUTAGEN_LABEL[item.mutagen] ?? item.mutagen;
    const star=item.starLabel ?? item.starId ?? '星曜';
    const from=item.sourcePalaceLabel ?? item.sourcePalaceId ?? '';
    const to=item.targetPalaceLabel ?? item.targetPalaceId ?? '';
    return `${mutagen}・${star}${from||to?`（${from}${from&&to?'→':''}${to}）`:''}`;
  }));
}function readingGuide(topic:TopicId,palaceLabel:string):string {
  const guides:Partial<Record<TopicId,string>>={
    personality:`先看${palaceLabel}主星與亮度，再看對宮、三方與四化是否補強或牽制。命宮不是只讀一顆主星；三方四正若出現不同性質星曜，通常要把「核心反應」與「外部情境」分開理解。`,
    career:`官祿宮先看工作角色的核心星曜，再合看財帛、命宮與遷移等三方四正。這能區分「本人做事方式」、「資源條件」與「外部工作環境」三個層次。`,
    wealth:`財帛宮不單讀收入，而是和命宮、官祿及對宮一起看資源如何取得、配置與保留；生年四化與宮干飛化則用來標示哪些資源議題容易被放大。`,
    relationship:`夫妻宮先讀互動核心，再把對宮與三方帶入；這能避免把單一星曜直接當成伴侶個性或婚姻結果，而是看關係中哪些互動模式比較常被啟動。`,
    social:`僕役宮在古典語境與現代朋友並不完全等義，因此主要用來看合作、支援與分工結構，再配合三方四正判斷人際資源如何進入生活。`,
    family:`兄弟宮先看近身家庭與手足協調結構，再合看相關宮位。這裡重點是互動與分工，不直接判定親疏、孝順或家庭成敗。`,
    home:`田宅宮先看居住與空間配置，再看相關宮位與四化如何改變穩定、搬動、整理或長期維護的議題；不直接推論房價與投資結果。`,
    mobility:`遷移宮先看人在外部環境、移動與陌生情境中的運作方式，再合看命宮與三方四正。這裡讀的是環境切換與適應節奏，不預測出行吉凶、事故、財運或必然事件。`,
    wellbeing:`福德宮先看休整與內在節奏，再把三方四正與四化當成干擾或支持背景；這裡只做生活節奏的文化性觀察，不做心理或醫療診斷。`,
  };
  return guides[topic] ?? `先看${palaceLabel}本身，再把三方四正與四化一起閱讀。`;
}

export function buildTopicStructuralContext(chart:NormalizedChart,topic:TopicId):TopicStructuralContextViewModel|undefined {
  const palaceId=TOPIC_PALACE[topic]; if(!palaceId) return undefined;
  const evidence=buildPalaceEvidenceViewModel(chart,palaceId);
  const starChips=Object.freeze(evidence.stars.map(starText));
  const relationChips=Object.freeze(evidence.relatedPalaces.filter((item)=>item.relation==='trine'||item.relation==='opposite').map(relationText));
  const birth=transformationText(evidence.birthYearTransformations);
  const incoming=transformationText(evidence.incomingPalaceStemFlights);
  const outgoing=transformationText(evidence.outgoingPalaceStemFlights);  const transformationChips=Object.freeze([...birth,...incoming.map((x)=>`入：${x}`),...outgoing.map((x)=>`出：${x}`)]);
  const focalSummary=`${evidence.palaceLabel}${evidence.isBodyPalace?'（同為身宮）':''}落在${evidence.heavenlyStem}${evidence.earthlyBranch}，宮內可列星曜為：${listText(starChips,'目前沒有可列星曜')}。這一段只描述實際盤面結構，先確認「有哪些星、亮度與生年四化」，再進入規則式解讀。`;
  const relationSummary=relationChips.length
    ? `此宮的三方四正目前可直接看到：${relationChips.join('；')}。成熟解盤不應只讀本宮，因此後續判讀會把這些相關宮位當成同一主題的背景，而不是把它們各自拆成互不相干的句子。`
    : '目前沒有可列的三方／對宮結構資料；系統不會自行補造關聯。';
  const transformationSummary=transformationChips.length
    ? `四化與宮干飛化可列出：${transformationChips.join('；')}。這些項目只標示此主題在哪些地方被推動、聚焦或牽制，仍需與星曜、亮度及宮位關係一起閱讀。`
    : '此主題目前沒有可列的生年四化或宮干飛化條目；沒有資料時不以罐頭吉凶補滿。';
  return Object.freeze({
    model:'TOPIC-STRUCTURAL-CONTEXT-0.1.0', topic, palaceId, palaceLabel:evidence.palaceLabel,
    headline:TOPIC_HEADLINE[topic] ?? `${evidence.palaceLabel}盤面結構`, focalSummary, relationSummary,
    transformationSummary, readingGuide:readingGuide(topic,evidence.palaceLabel), starChips, relationChips,
    transformationChips, disclosure:'「盤面結構」層只呈現客觀排盤資料與閱讀順序；真正的性格、事業、財運、感情等結論仍只由已核准規則產生。',
  });
}

export function buildTopicStructuralContexts(chart:NormalizedChart,topics:readonly TopicId[]):readonly TopicStructuralContextViewModel[] {
  return Object.freeze(topics.map((topic)=>buildTopicStructuralContext(chart,topic)).filter((item):item is TopicStructuralContextViewModel=>Boolean(item)));
}